import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import AdminCustomButton from 'app/components/AdminCustomButton';

const CropperWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const AspectRatioControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
`;

const AspectLabel = styled.label`
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
  min-width: 80px;
`;

const AspectRatioButton = styled.button`
  padding: 6px 12px;
  background-color: #f3f4f6;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #e5e7eb;
    border-color: #9ca3af;
  }

  &.active {
    background-color: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }
`;

const AspectRatioGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const CropperContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  max-width: 100%;
  max-height: 400px;
  background-color: #f3f4f6;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  margin: 10px 0;

  .ReactCrop {
    display: inline-block;
    max-width: 100%;
  }

  .ReactCrop__child-wrapper {
    max-height: 400px;
  }
`;

const CropperControls = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
  justify-content: flex-end;
`;

const ErrorMessage = styled.div`
  padding: 12px 16px;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
`;

export interface AspectRatioOption {
    value: number | undefined;
    label: string;
    title: string;
}

interface ImageCropperProps {
    imageUrl: string;
    onCropComplete: (croppedImageBlob: Blob, croppedImageUrl: string) => void;
    onCancel: () => void;
    aspectRatioOptions?: AspectRatioOption[];
    defaultAspectRatio?: number | undefined;
    minWidth?: number;
    minHeight?: number;
    showAspectRatioControls?: boolean;
    cropButtonLabel?: string;
    cancelButtonLabel?: string;
    allowedFormats?: string[];
    onFormatError?: (message: string) => void;
}

const defaultAspectRatioOptions: AspectRatioOption[] = [
    { value: undefined, label: 'Free Form', title: 'Free form - drag corners to resize' },
    { value: 1, label: 'Square (1:1)', title: 'Square crop area' },
    { value: 16 / 9, label: 'Wide (16:9)', title: 'Wide rectangle' },
    { value: 4 / 3, label: 'Standard (4:3)', title: 'Standard rectangle' },
    { value: 0.5, label: 'Portrait (1:2)', title: 'Portrait - tall rectangle' },
];

// Allowed image formats
const DEFAULT_ALLOWED_FORMATS = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

const AdminCustomImageCropper: React.FC<ImageCropperProps> = ({
    imageUrl,
    onCropComplete,
    onCancel,
    aspectRatioOptions = defaultAspectRatioOptions,
    defaultAspectRatio = 1,
    minWidth = 50,
    minHeight = 50,
    showAspectRatioControls = true,
    cropButtonLabel = 'Crop & Continue',
    cancelButtonLabel = 'Cancel',
    allowedFormats = DEFAULT_ALLOWED_FORMATS,
    onFormatError,
}) => {
    const MAX_OUTPUT_SIZE = 10 * 1024 * 1024;
    const MAX_DIMENSION = 4096;
    const imgRef = useRef<HTMLImageElement>(null);
    const [crop, setCrop] = useState<Crop>({ unit: '%', width: 50, height: 50, x: 25, y: 25 });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
    const [aspectRatio, setAspectRatio] = useState<number | undefined>(defaultAspectRatio);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [formatError, setFormatError] = useState<string>('');
    const [isCropping, setIsCropping] = useState(false);

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    // Validate image format
    useEffect(() => {
        const validateImageFormat = async () => {
            try {
                // Fetch the image to get its actual MIME type
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const mimeType = blob.type;

                if (!allowedFormats.includes(mimeType)) {
                    const allowedExtensions = allowedFormats
                        .map(format => format.split('/')[1].toUpperCase())
                        .join(', ');
                    const errorMsg = `Invalid image format. Only ${allowedExtensions} files are allowed.`;
                    setFormatError(errorMsg);

                    if (onFormatError) {
                        onFormatError(errorMsg);
                    }
                } else {
                    setFormatError('');
                }
            } catch (error) {
                console.error('Error validating image format:', error);
            }
        };

        if (imageUrl) {
            validateImageFormat();
        }
    }, [imageUrl, allowedFormats, onFormatError]);

    // Update crop when aspect ratio changes
    useEffect(() => {
        if (imageLoaded && imgRef.current) {
            const imageWidth = imgRef.current.width;
            const imageHeight = imgRef.current.height;

            let newCrop: Crop;

            if (aspectRatio === undefined) {
                // Free form - use 90% of image with 5% padding
                const cropWidth = imageWidth * 0.9;
                const cropHeight = imageHeight * 0.9;
                const x = imageWidth * 0.05;
                const y = imageHeight * 0.05;

                newCrop = {
                    unit: 'px',
                    width: cropWidth,
                    height: cropHeight,
                    x: x,
                    y: y,
                };
            } else if (aspectRatio === 1) {
                // Square (1:1) - 80% of smaller dimension
                const size = Math.min(imageWidth, imageHeight) * 0.8;
                const x = (imageWidth - size) / 2;
                const y = (imageHeight - size) / 2;

                newCrop = {
                    unit: 'px',
                    width: size,
                    height: size,
                    x: x,
                    y: y,
                };
            } else if (aspectRatio === 16 / 9) {
                // Wide (16:9)
                const cropWidth = imageWidth * 0.9;
                const cropHeight = cropWidth / (16 / 9);
                const x = imageWidth * 0.05;
                const y = (imageHeight - cropHeight) / 2;

                newCrop = {
                    unit: 'px',
                    width: cropWidth,
                    height: cropHeight,
                    x: x,
                    y: y,
                };
            } else if (aspectRatio === 4 / 3) {
                // Standard (4:3)
                const cropWidth = imageWidth * 0.9;
                const cropHeight = cropWidth / (4 / 3);
                const x = imageWidth * 0.05;
                const y = (imageHeight - cropHeight) / 2;

                newCrop = {
                    unit: 'px',
                    width: cropWidth,
                    height: cropHeight,
                    x: x,
                    y: y,
                };
            } else if (aspectRatio === 0.5) {
                // Portrait (1:2)
                const cropHeight = Math.min(imageHeight * 0.8, imageHeight - 40);
                const cropWidth = cropHeight * 0.5;
                const x = (imageWidth - cropWidth) / 2;
                const y = (imageHeight - cropHeight) / 2;

                newCrop = {
                    unit: 'px',
                    width: cropWidth,
                    height: cropHeight,
                    x: x,
                    y: y,
                };
            } else {
                // Custom aspect ratio
                const cropWidth = imageWidth * 0.9;
                const cropHeight = cropWidth / aspectRatio;
                const x = imageWidth * 0.05;
                const y = (imageHeight - cropHeight) / 2;

                newCrop = {
                    unit: 'px',
                    width: cropWidth,
                    height: cropHeight,
                    x: x,
                    y: y,
                };
            }

            setCrop(newCrop);

            // Also set completedCrop immediately with the initial crop dimensions
            // This enables the Crop button right away
            if (newCrop.unit === 'px') {
                setCompletedCrop({
                    unit: 'px',
                    x: newCrop.x,
                    y: newCrop.y,
                    width: newCrop.width,
                    height: newCrop.height,
                });
            }
        }
    }, [aspectRatio, imageLoaded]);



    const handleCropComplete = async () => {
        if (!imageUrl || !completedCrop || !imgRef.current || formatError || isCropping) return;

        setIsCropping(true);

        // Defer heavy canvas work so the loader renders first
        setTimeout(async () => {
            try {
                const image = imgRef.current!;
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                const scaleX = image.naturalWidth / image.width;
                const scaleY = image.naturalHeight / image.height;

                let cropWidth = completedCrop.width * scaleX;
                let cropHeight = completedCrop.height * scaleY;

                if (cropWidth > MAX_DIMENSION || cropHeight > MAX_DIMENSION) {
                    const downScale = MAX_DIMENSION / Math.max(cropWidth, cropHeight);
                    cropWidth = Math.round(cropWidth * downScale);
                    cropHeight = Math.round(cropHeight * downScale);
                }

                canvas.width = cropWidth;
                canvas.height = cropHeight;

                if (ctx) {
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(
                        image,
                        completedCrop.x * scaleX,
                        completedCrop.y * scaleY,
                        completedCrop.width * scaleX,
                        completedCrop.height * scaleY,
                        0,
                        0,
                        cropWidth,
                        cropHeight
                    );
                }

                const createBlob = (type: string, quality?: number): Promise<Blob | null> =>
                    new Promise((resolve) => canvas.toBlob(resolve, type, quality));

                let blob = await createBlob('image/png');

                if (blob && blob.size > MAX_OUTPUT_SIZE) {
                    blob = await createBlob('image/jpeg', 0.9);
                }

                if (blob) {
                    const croppedImageUrl = canvas.toDataURL(
                        blob.type === 'image/jpeg' ? 'image/jpeg' : 'image/png',
                        blob.type === 'image/jpeg' ? 0.9 : undefined
                    );
                    onCropComplete(blob, croppedImageUrl);
                }
            } catch (error) {
                console.error('Error cropping image:', error);
            } finally {
                setIsCropping(false);
            }
        }, 50);
    };

    return (
        <CropperWrapper>
            {formatError && (
                <ErrorMessage>{formatError}</ErrorMessage>
            )}

            {showAspectRatioControls && !formatError && (
                <AspectRatioControls>
                    <AspectLabel>Crop Shape:</AspectLabel>
                    <AspectRatioGroup>
                        {aspectRatioOptions.map((option) => (
                            <AspectRatioButton
                                key={option.label}
                                className={aspectRatio === option.value ? 'active' : ''}
                                onClick={() => setAspectRatio(option.value)}
                                title={option.title}
                            >
                                {option.label}
                            </AspectRatioButton>
                        ))}
                    </AspectRatioGroup>
                </AspectRatioControls>
            )}

            {!formatError && (
                <CropperContainer>
                    <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={aspectRatio}
                        minWidth={minWidth}
                        minHeight={minHeight}
                    >
                        <img
                            ref={imgRef}
                            src={imageUrl}
                            alt="Crop"
                            onLoad={handleImageLoad}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '400px',
                                width: 'auto',
                                height: 'auto',
                            }}
                        />
                    </ReactCrop>
                </CropperContainer>
            )}

            <CropperControls>
                <AdminCustomButton
                    buttonType="cancel"
                    onClick={onCancel}
                    disabled={!completedCrop || !!formatError || isCropping}
                >
                    {cancelButtonLabel}
                </AdminCustomButton>
                <AdminCustomButton
                    buttonType="primary"
                    className="button-style"
                    onClick={handleCropComplete}
                    disabled={!completedCrop || !!formatError || isCropping}
                >
                    {isCropping ? 'Cropping...' : cropButtonLabel}
                </AdminCustomButton>
            </CropperControls>
        </CropperWrapper>
    );
};

export default AdminCustomImageCropper;